import { UsageWidget } from "@/components/account/UsageWidget";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { BillingInfo, InvoiceRecord } from "@/lib/api/routes/billing-portal";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Download,
  ExternalLink,
  Loader2,
  XCircle,
} from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

function tierLabel(tier: string): string {
  switch (tier) {
    case "trial":
      return "Trial";
    case "starter":
    case "small":
      return "Basic";
    case "professional":
    case "unlimited":
      return "Premium";
    case "enterprise":
      return "Enterprise";
    case "legacy_professional":
      return "Professional (Legacy)";
    default:
      return tier;
  }
}

function tierBadgeVariant(tier: string): "default" | "outline" | "secondary" | "destructive" {
  switch (tier) {
    case "trial":
      return "outline";
    case "starter":
    case "small":
      return "secondary";
    case "professional":
    case "unlimited":
    case "legacy_professional":
      return "default";
    case "enterprise":
      return "default";
    default:
      return "outline";
  }
}

const TIER_PRICES: Record<string, string> = {
  starter: "$150/mo",
  small: "$150/mo",
  professional: "$750/mo",
  unlimited: "$750/mo",
  enterprise: "Custom",
};

const TIER_FEATURES: Record<string, string[]> = {
  trial: [
    "50 quotes/month",
    "10 shipments/month",
    "3 carriers",
    "5 templates",
    "100 API calls/day",
  ],
  starter: [
    "200 quotes/month",
    "50 shipments/month",
    "10 carriers",
    "20 templates",
    "500 API calls/day",
  ],
  small: [
    "200 quotes/month",
    "50 shipments/month",
    "10 carriers",
    "20 templates",
    "500 API calls/day",
  ],
  professional: [
    "Unlimited quotes",
    "Unlimited shipments",
    "Unlimited carriers",
    "Unlimited templates",
    "5,000 API calls/day",
    "AI recommendations",
    "Custom reports",
    "Team collaboration (10 users)",
  ],
  unlimited: [
    "Unlimited quotes",
    "Unlimited shipments",
    "Unlimited carriers",
    "Unlimited templates",
    "5,000 API calls/day",
    "AI recommendations",
    "Custom reports",
    "Team collaboration (10 users)",
  ],
  enterprise: [
    "Everything in Premium",
    "Unlimited API calls",
    "Unlimited team members",
    "Dedicated support",
    "Custom integrations",
  ],
  legacy_professional: [
    "Unlimited quotes",
    "Unlimited shipments",
    "Unlimited carriers",
    "Unlimited templates",
    "Unlimited API calls",
    "All features included",
  ],
};

function daysRemaining(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null;
  const diff = new Date(trialEndsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function invoiceStatusBadge(status: string) {
  switch (status) {
    case "paid":
      return (
        <Badge variant="default" className="bg-emerald-600">
          <CheckCircle2 className="mr-1 size-3" />
          Paid
        </Badge>
      );
    case "open":
      return (
        <Badge variant="outline" className="border-yellow-500 text-yellow-600">
          <AlertTriangle className="mr-1 size-3" />
          Open
        </Badge>
      );
    default:
      return (
        <Badge variant="destructive">
          <XCircle className="mr-1 size-3" />
          {status}
        </Badge>
      );
  }
}

export default function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [billing, setBilling] = React.useState<BillingInfo | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [cancelLoading, setCancelLoading] = React.useState(false);
  const [cancelConfirm, setCancelConfirm] = React.useState(false);
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);

  // Check for success/canceled query params from Stripe checkout redirect
  const checkoutSuccess = searchParams.get("success") === "true";
  const checkoutCanceled = searchParams.get("canceled") === "true";

  React.useEffect(() => {
    if (checkoutSuccess || checkoutCanceled) {
      // Clear query params after showing the message
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [checkoutSuccess, checkoutCanceled, setSearchParams]);

  const fetchBilling = React.useCallback(async () => {
    try {
      const res = await fetch("/api/account/billing/info", { credentials: "include" });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setError((json as { error?: string }).error ?? "Unable to load billing info.");
        return;
      }
      const json = await res.json();
      if (json.status === "success") {
        setBilling(json.data);
        setError(null);
      } else {
        setError(json.error ?? "Unable to load billing info.");
      }
    } catch {
      setError("Unable to load billing info.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void fetchBilling();
  }, [fetchBilling]);

  const onManagePayment = async () => {
    setPortalLoading(true);
    setActionMessage(null);
    try {
      const res = await fetch("/api/account/billing/portal", {
        method: "POST",
        credentials: "include",
      });
      const json = await res.json();
      if (json.status === "success" && json.data?.portal_url) {
        window.location.href = json.data.portal_url;
      } else {
        setActionMessage(json.error ?? "Unable to open payment management.");
      }
    } catch {
      setActionMessage("Unable to open payment management.");
    } finally {
      setPortalLoading(false);
    }
  };

  const onCancelSubscription = async () => {
    setCancelLoading(true);
    setActionMessage(null);
    try {
      const res = await fetch("/api/account/billing/cancel", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (json.status === "success" && json.data?.canceled) {
        const cancelDate = json.data.cancel_at ? formatDate(json.data.cancel_at) : "end of billing period";
        setActionMessage(`Subscription will be canceled on ${cancelDate}. You will retain access until then.`);
        setCancelConfirm(false);
        void fetchBilling();
      } else {
        setActionMessage(json.error ?? "Unable to cancel subscription.");
      }
    } catch {
      setActionMessage("Unable to cancel subscription.");
    } finally {
      setCancelLoading(false);
    }
  };

  const openUpgradeModal = () => {
    window.dispatchEvent(new CustomEvent("shippeek:open-upgrade-modal"));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <UsageWidget />
        <Card>
          <CardHeader>
            <CardTitle>Billing & Subscription</CardTitle>
            <CardDescription>Loading billing information...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !billing) {
    return (
      <div className="space-y-6">
        <UsageWidget />
        <Card>
          <CardHeader>
            <CardTitle>Billing & Subscription</CardTitle>
            <CardDescription>Unable to load billing information.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-destructive">{error ?? "Unknown error"}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => { setLoading(true); void fetchBilling(); }}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isTrial = billing.subscription_tier === "trial";
  const isLegacy = billing.is_legacy;
  const isPaid = ["starter", "professional", "small", "unlimited", "enterprise"].includes(billing.subscription_tier);
  const days = daysRemaining(billing.trial_ends_at);
  const features = TIER_FEATURES[billing.subscription_tier] ?? [];
  const canCancel = isPaid && (billing.stripe_subscription_id || billing.polar_subscription_id);
  const canManagePayment = !!billing.stripe_customer_id;

  return (
    <div className="space-y-6">
      {/* Checkout result banners */}
      {checkoutSuccess ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/40">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="size-5 text-emerald-600" />
            <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
              Payment successful! Your plan has been upgraded. It may take a moment to reflect.
            </p>
          </div>
        </div>
      ) : null}

      {checkoutCanceled ? (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-950/40">
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-yellow-600" />
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Checkout was canceled. No changes were made to your plan.
            </p>
          </div>
        </div>
      ) : null}

      <UsageWidget />

      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <CardTitle>Current Plan</CardTitle>
                <Badge variant={tierBadgeVariant(billing.subscription_tier)}>
                  {tierLabel(billing.subscription_tier)}
                </Badge>
              </div>
              <CardDescription>
                {isTrial && days !== null
                  ? `Trial expires in ${days} day${days !== 1 ? "s" : ""}. Upgrade to keep your data and unlock unlimited access.`
                  : isLegacy
                    ? "You are on the Legacy Professional plan with unlimited access at no cost."
                    : isPaid
                      ? `${tierLabel(billing.subscription_tier)} plan ${TIER_PRICES[billing.subscription_tier] ? `at ${TIER_PRICES[billing.subscription_tier]}` : ""}`
                      : "Manage your subscription and billing."}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Plan details */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground">Plan Features</h4>
              <ul className="space-y-1.5">
                {features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="size-3.5 shrink-0 text-emerald-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground">Billing Details</h4>
              <dl className="space-y-2 text-sm">
                {isPaid && billing.subscription_renewal_date ? (
                  <div>
                    <dt className="text-muted-foreground">Next billing date</dt>
                    <dd className="font-medium">{formatDate(billing.subscription_renewal_date)}</dd>
                  </div>
                ) : null}
                {isPaid && TIER_PRICES[billing.subscription_tier] ? (
                  <div>
                    <dt className="text-muted-foreground">Amount</dt>
                    <dd className="font-medium">{TIER_PRICES[billing.subscription_tier]}</dd>
                  </div>
                ) : null}
                {isPaid && billing.subscription_started_at ? (
                  <div>
                    <dt className="text-muted-foreground">Member since</dt>
                    <dd className="font-medium">{formatDate(billing.subscription_started_at)}</dd>
                  </div>
                ) : null}
                {isTrial && billing.trial_ends_at ? (
                  <div>
                    <dt className="text-muted-foreground">Trial expires</dt>
                    <dd className="font-medium">{formatDate(billing.trial_ends_at)}</dd>
                  </div>
                ) : null}
                {isLegacy ? (
                  <div>
                    <dt className="text-muted-foreground">Plan type</dt>
                    <dd className="font-medium">Grandfathered unlimited access</dd>
                  </div>
                ) : null}
              </dl>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 border-t pt-4">
            {(isTrial || isPaid) ? (
              <Button onClick={openUpgradeModal}>
                {isTrial ? "Upgrade now" : "Change plan"}
                <ArrowRight className="ml-1 size-3.5" />
              </Button>
            ) : null}

            {canManagePayment ? (
              <Button
                variant="outline"
                onClick={() => void onManagePayment()}
                disabled={portalLoading}
              >
                {portalLoading ? (
                  <Loader2 className="mr-1 size-3.5 animate-spin" />
                ) : (
                  <CreditCard className="mr-1 size-3.5" />
                )}
                Manage payment method
              </Button>
            ) : null}

            {canCancel ? (
              cancelConfirm ? (
                <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2">
                  <p className="text-sm text-destructive">Cancel at end of billing period?</p>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => void onCancelSubscription()}
                    disabled={cancelLoading}
                  >
                    {cancelLoading ? <Loader2 className="mr-1 size-3 animate-spin" /> : null}
                    Confirm
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCancelConfirm(false)}
                    disabled={cancelLoading}
                  >
                    Keep plan
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setCancelConfirm(true)}
                >
                  Cancel subscription
                </Button>
              )
            ) : null}
          </div>

          {actionMessage ? (
            <p className="text-sm text-muted-foreground">{actionMessage}</p>
          ) : null}
        </CardContent>
      </Card>

      {/* Billing History Card */}
      <Card>
        <CardHeader>
          <CardTitle>Billing History</CardTitle>
          <CardDescription>
            {billing.invoices.length > 0
              ? "Past invoices and payment records."
              : "No billing history yet."}
          </CardDescription>
        </CardHeader>
        {billing.invoices.length > 0 ? (
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">Date</th>
                    <th className="pb-2 pr-4 font-medium">Period</th>
                    <th className="pb-2 pr-4 font-medium">Amount</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {billing.invoices.map((invoice) => (
                    <InvoiceRow key={invoice.id} invoice={invoice} />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        ) : (
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {isTrial
                ? "Invoices will appear here once you upgrade to a paid plan."
                : isLegacy
                  ? "Legacy plans have no billing history."
                  : "No invoices found."}
            </p>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: InvoiceRecord }) {
  const period = invoice.period_start && invoice.period_end
    ? `${formatDate(invoice.period_start)} - ${formatDate(invoice.period_end)}`
    : "N/A";

  return (
    <tr>
      <td className="py-3 pr-4 tabular-nums">{formatDate(invoice.created_at)}</td>
      <td className="py-3 pr-4 text-muted-foreground">{period}</td>
      <td className="py-3 pr-4 tabular-nums font-medium">
        {formatCurrency(invoice.amount_cents, invoice.currency)}
      </td>
      <td className="py-3 pr-4">{invoiceStatusBadge(invoice.status)}</td>
      <td className="py-3">
        <div className="flex gap-2">
          {invoice.invoice_url ? (
            <a
              href={invoice.invoice_url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "inline-flex items-center gap-1 text-sm text-primary hover:underline",
              )}
            >
              <ExternalLink className="size-3" />
              View
            </a>
          ) : null}
          {invoice.invoice_pdf_url ? (
            <a
              href={invoice.invoice_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <Download className="size-3" />
              PDF
            </a>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
